// The Call-to-Claim pipeline seam: audio -> transcript -> extracted AnswerSet.
//
// Hybrid fidelity (agreed in brainstorming): speech-to-text is pre-baked from a
// sample call; the LLM extraction is the live part. Both sit behind this seam so
// the UI never changes when the live calls plug in.

import type { AnswerSet } from './lib/types';
import { SAMPLE_TRANSCRIPT, CANNED_ANSWERS } from './fixtures';

const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));

const TRANSCRIBE_MS = 1500;
const EXTRACT_FALLBACK_MS = 2400;

/**
 * Transcribe the uploaded recording.
 * Pre-baked for the demo; real STT (e.g. Whisper via /api/transcribe) drops in here.
 */
export async function transcribe(_file: File | null): Promise<string> {
  await delay(TRANSCRIBE_MS);
  return SAMPLE_TRANSCRIPT;
}

// React StrictMode double-mounts Processing, so extract() can fire more than
// once concurrently; share one in-flight request so we never pay for the
// (expensive) live extraction twice.
let inflight: Promise<AnswerSet> | null = null;

/**
 * Extract AA answers from the transcript.
 * Live Claude extraction via the dev-server /api/extract route; falls back to
 * the canned set on any failure so the flow always works.
 */
export function extract(transcript: string): Promise<AnswerSet> {
  if (!inflight) {
    inflight = doExtract(transcript).finally(() => {
      inflight = null;
    });
  }
  return inflight;
}

async function doExtract(transcript: string): Promise<AnswerSet> {
  try {
    const res = await fetch('/api/extract', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ transcript }),
    });
    const data = (await res.json()) as {
      answers?: AnswerSet;
      groups?: unknown;
      error?: string;
    };
    if (res.ok && data.answers && Object.keys(data.answers).length) {
      console.info('[aa-claim] live extraction', data.groups);
      return data.answers;
    }
    console.info(
      '[aa-claim] falling back to canned answers:',
      data.error ?? `HTTP ${res.status}`,
    );
  } catch (e) {
    console.info(
      '[aa-claim] falling back to canned answers:',
      (e as Error).message,
    );
  }
  await delay(EXTRACT_FALLBACK_MS);
  return CANNED_ANSWERS;
}
